import type { FC, ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';

import { DashboardWidgetType } from 'src/constants/dashboard';
import { SECTION } from 'src/constants/section';
import type {
  AppendedWidgetsConfig,
  DashboardConfig,
  DashboardFilters,
} from 'src/types/dashboard';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import { useSection } from 'src/components/app/context/SectionContext';
import { useUserContext } from 'src/components/app/context/UserContext';
import { dashboardContentLoading } from 'src/components/dashboard/DashboardPage/atom';
import { getSelectionMode } from 'src/components/dashboard/DashboardPage/helper';

export interface DashboardContextValue {
  dashboardConfig?: DashboardConfig;
  filters: DashboardFilters;
  loading: boolean;
  widgetConfigs: AppendedWidgetsConfig[];
  orgTreeMode?: number;
}

export const DashboardContext = createContext<DashboardContextValue>({
  dashboardConfig: undefined,
  filters: {} as DashboardFilters,
  loading: false,
  widgetConfigs: [],
  orgTreeMode: undefined,
});

interface DashboardProviderProps {
  children: ReactNode;
}

const DashboardProvider: FC<DashboardProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();
  const { moduleId, section, sectionIsLoaded } = useSection();
  const { user, currentSurvey } = useUserContext();
  const setContentLoading = useSetAtom(dashboardContentLoading);
  const endPoint = generatePath(apiPaths.DASHBOARD.GET_BY_MODULE_ID, {
    moduleId,
  });

  const {
    data: dashboardConfig,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      endPoint,
      i18n.language,
      moduleId,
      currentSurvey,
      user ? user.id : false,
    ],
    staleTime: 5 * 60 * 1000,
    enabled: sectionIsLoaded && section === SECTION.DASHBOARD && !!moduleId,
    queryFn: ({ signal }) =>
      HttpService.get(endPoint, undefined, undefined, { signal })
        .then((response: { data: DashboardConfig }) => response.data)
        .catch(() => Promise.reject()),
  });

  const loading = useMemo(
    () => (!moduleId ? false : isLoading || isFetching),
    [isLoading, isFetching, moduleId],
  );

  useEffect(() => {
    setContentLoading(loading);
  }, [loading]);

  const widgetConfigs: AppendedWidgetsConfig[] = useMemo(() => {
    if (!dashboardConfig) return [];
    let currentSection: number | undefined;
    let currentWidgetType: DashboardWidgetType;
    return dashboardConfig.widgets.map((config) => {
      const isFirstOfWidgetType = currentWidgetType !== config.widget_type;
      if (config.widget_type === DashboardWidgetType.SECTION) {
        currentSection = config.id;
      }
      currentWidgetType = config.widget_type;
      return { ...config, isFirstOfWidgetType, currentSection };
    });
  }, [dashboardConfig]);

  const orgTreeMode = useMemo(
    () => getSelectionMode(dashboardConfig),
    [dashboardConfig],
  );

  const filters: DashboardFilters = useMemo(() => {
    if (!dashboardConfig?.filters) return {} as DashboardFilters;

    return dashboardConfig.filters.reduce(
      (acc: DashboardFilters, values) => ({
        [values.filter_type]: {
          initiallyActive: values.initially_active,
        },
        ...acc,
      }),
      {} as DashboardFilters,
    );
  }, [dashboardConfig]);

  return (
    <DashboardContext.Provider
      value={{
        dashboardConfig,
        filters,
        widgetConfigs,
        orgTreeMode,
        loading,
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export const useDashboardContext = () => useContext(DashboardContext);

export default DashboardProvider;
